import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CopyButton, Field, LoadingState, PlugIcon, TrashIcon, useConfirm, useToast } from "@signet/ui";
import { ago, api, useMe } from "@signet/web-kit";

/* Your own account: password, authenticator, remembered devices, and the
   tokens that let Claude Desktop or Claude Code use Signet as you. */

type Settings = {
  factors: { totpEnrolled: boolean; recoveryRemaining: number };
  trustedDevices: { id: string; label: string | null; createdAt: string; expiresAt: string }[];
  tokens: { id: string; name: string; createdAt: string; lastUsedAt: string | null }[];
};

export function SettingsPage() {
  const { me } = useMe();
  const settings = useQuery({ queryKey: ["settings"], queryFn: () => api<Settings>("/api/settings") });
  if (settings.isLoading || !settings.data) return <div className="page"><LoadingState /></div>;
  return (
    <div className="page narrow-page">
      <div className="page-head"><div><h1>Settings</h1><p>Signed in as {me?.user.name && me.user.name !== me.user.username ? `${me.user.name} (${me.user.username})` : me?.user.username}.</p></div></div>
      <Password />
      <Authenticator data={settings.data} />
      <Tokens data={settings.data} />
      {me?.user.isOwner ? <AiKey /> : null}
    </div>
  );
}

function Password() {
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [error, setError] = useState<string | null>(null);
  async function save() {
    setError(null);
    try {
      await api("/api/settings/password", { method: "POST", body: { current, next } });
      setCurrent("");
      setNext("");
      toast.push({ tone: "good", title: "Password changed", body: "You're signed out everywhere else." });
    } catch (err) {
      setError((err as Error).message);
    }
  }
  return (
    <section className="panel">
      <div className="panel-head"><div><h2>Password</h2><p>The same password signs you in to reporting and the creators hub.</p></div></div>
      <div className="panel-body col">
        <Field label="Current password"><input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} /></Field>
        <Field label="New password" hint="At least 10 characters." error={error}><input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} /></Field>
        <div><button type="button" className="secondary-button" disabled={!current || next.length < 10} onClick={save}>Change password</button></div>
      </div>
    </section>
  );
}

function Authenticator({ data }: { data: Settings }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  async function regenerate() {
    try {
      setCodes((await api<{ recoveryCodes: string[] }>("/api/settings/recovery-codes", { method: "POST", body: { code } })).recoveryCodes);
      setCode("");
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    } catch (error) {
      toast.push({ tone: "bad", title: "Couldn't make new codes", body: (error as Error).message });
    }
  }
  async function forget() {
    await api("/api/settings/trusted-devices", { method: "DELETE" });
    queryClient.invalidateQueries({ queryKey: ["settings"] });
    toast.push({ tone: "good", title: "Remembered devices forgotten", body: "Every device asks for a code at its next sign-in." });
  }
  return (
    <section className="panel">
      <div className="panel-head"><div><h2>Authenticator and recovery codes</h2><p>{data.factors.recoveryRemaining} of 10 recovery codes left. Each works once.</p></div></div>
      <div className="panel-body col">
        {codes ? (
          <>
            <p>Your new recovery codes. The old ones no longer work, and these aren't shown again.</p>
            <ol className="recovery-codes">{codes.map((c) => <li key={c}><code>{c}</code></li>)}</ol>
            <div><CopyButton value={codes.join("\n")} label="Copy codes" className="secondary-button tiny">Copy all</CopyButton></div>
          </>
        ) : (
          <div className="row wrap">
            <Field label="Code from your authenticator"><input inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} /></Field>
            <button type="button" className="secondary-button" disabled={code.replace(/\s/g, "").length < 6} onClick={regenerate}>Make new recovery codes</button>
          </div>
        )}
        <div className="signet-gap" />
        <strong>Remembered devices</strong>
        {data.trustedDevices.length ? (
          <>
            <ul className="plain-list">{data.trustedDevices.map((d) => <li key={d.id}>{d.label ?? "Unknown device"} <span className="faint">until {new Date(d.expiresAt).toLocaleDateString()}</span></li>)}</ul>
            <div><button type="button" className="secondary-button tiny" onClick={forget}>Forget all devices</button></div>
          </>
        ) : <p className="faint">None. Every sign-in asks for a code.</p>}
      </div>
    </section>
  );
}

function Tokens({ data }: { data: Settings }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { confirm, element } = useConfirm();
  const [name, setName] = useState("Claude Desktop");
  const [made, setMade] = useState<string | null>(null);
  const endpoint = `${location.origin.replace(":5173", ":8787")}/api/mcp`;

  async function create() {
    try {
      setMade((await api<{ token: string }>("/api/settings/tokens", { method: "POST", body: { name } })).token);
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    } catch (error) {
      toast.push({ tone: "bad", title: "Couldn't make a token", body: (error as Error).message });
    }
  }
  async function revoke(id: string, label: string) {
    if (!(await confirm({ title: `Revoke ${label}?`, body: "Anything using it stops working straight away.", confirmLabel: "Revoke token", danger: true }))) return;
    await api(`/api/settings/tokens/${id}`, { method: "DELETE" });
    queryClient.invalidateQueries({ queryKey: ["settings"] });
  }

  const claudeCode = made ? `claude mcp add --transport http signet ${endpoint} --header "Authorization: Bearer ${made}"` : "";
  const desktop = made ? JSON.stringify({ mcpServers: { signet: { type: "http", url: endpoint, headers: { Authorization: `Bearer ${made}` } } } }, null, 2) : "";

  return (
    <section className="panel">
      {element}
      <div className="panel-head">
        <div>
          <h2><PlugIcon size={14} /> Use Signet from Claude</h2>
          <p>A personal token lets Claude Desktop or Claude Code use Signet's tools as you, with exactly your permissions. Anything that changes data waits for your approval in Signet.</p>
        </div>
      </div>
      <div className="panel-body col">
        {made ? (
          <>
            <p>Copy it now. Signet only stores a fingerprint of it, so it can't be shown again.</p>
            <div className="secret-box"><code>{made}</code><CopyButton value={made} label="Copy token" /></div>
            <Field label="Claude Code"><div className="secret-box"><code className="wrap">{claudeCode}</code><CopyButton value={claudeCode} label="Copy command" /></div></Field>
            <Field label="Claude Desktop config"><div className="secret-box"><pre>{desktop}</pre><CopyButton value={desktop} label="Copy config" /></div></Field>
            <div><button type="button" className="text-button" onClick={() => setMade(null)}>Done</button></div>
          </>
        ) : (
          <div className="row wrap">
            <Field label="Name it after where you'll use it"><input value={name} onChange={(e) => setName(e.target.value)} /></Field>
            <button type="button" className="secondary-button" disabled={!name.trim()} onClick={create}>Make a token</button>
          </div>
        )}
        {data.tokens.length ? (
          <ul className="plain-list">
            {data.tokens.map((t) => (
              <li key={t.id} className="row">
                <strong>{t.name}</strong>
                <span className="faint">{t.lastUsedAt ? `used ${ago(t.lastUsedAt)}` : "never used"}</span>
                <div className="spacer" />
                <button type="button" className="icon-button ghost small danger" aria-label={`Revoke ${t.name}`} onClick={() => revoke(t.id, t.name)}><TrashIcon size={13} /></button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}

function AiKey() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const source = useQuery({ queryKey: ["settings", "ai"], queryFn: () => api<{ source: "env" | "stored" | "missing" }>("/api/settings/ai") });
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  async function save(value: string | null) {
    setBusy(true);
    try {
      await api("/api/settings/ai", { method: "PUT", body: { apiKey: value } });
      setKey("");
      queryClient.invalidateQueries();
      toast.push({ tone: "good", title: value ? "Key checked and saved" : "Key removed" });
    } catch (error) {
      toast.push({ tone: "bad", title: "Couldn't save the key", body: (error as Error).message });
    } finally {
      setBusy(false);
    }
  }
  const state = source.data?.source;
  return (
    <section className="panel" id="ai">
      <div className="panel-head">
        <div>
          <h2>Anthropic API key</h2>
          <p>
            {state === "env" ? "Set on the server (ANTHROPIC_API_KEY), so it can't be changed here." : state === "stored" ? "Saved and encrypted. The assistant is on." : "Not set. The assistant is off."}
          </p>
        </div>
      </div>
      {state !== "env" ? (
        <div className="panel-body row wrap">
          <Field label={state === "stored" ? "Replace the key" : "Key"} hint="From console.anthropic.com. It's tested with one tiny request before it's saved.">
            <input type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder="sk-ant-..." />
          </Field>
          <button type="button" className="secondary-button" disabled={!key.trim() || busy} onClick={() => save(key.trim())}>{busy ? "Checking" : "Save key"}</button>
          {state === "stored" ? <button type="button" className="text-button danger" onClick={() => save(null)}>Remove key</button> : null}
        </div>
      ) : null}
    </section>
  );
}
